import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, ImagePlus, Star, Trash, AlertCircle, Check, Loader2 } from 'lucide-react';
import VariantImageManager from './VariantImageManager';
import { isHeicFile, convertHeicToJpeg } from '../utils/heicConvert';

export interface ProductImage {
  id: number;
  url: string;
  principal: boolean;
  orden: number;
  producto_variante_id: number;
  variante_id?: string;
  tempFile?: File; // For temporary files before upload
  fileName?: string; // Original filename for saving
}

export interface Variant {
  id: string;
  nombre: string;
  image_url?: string;
  images?: ProductImage[];
  created_at?: string; // Database timestamp for ordering variants
}

interface VariantFormProps {
  editingVariant: Variant | null;
  newProduct: { nombre: string; images: ProductImage[] };
  loading: boolean;
  uploadingImages: Record<string, boolean>;
  imageError: string | null;
  onCancel: () => void;
  onSave: () => void; // This will be our unified save function for both adding and updating
  onImageUpload: (variantId: string, file: File) => void;
  onDeleteImage: (variantId: string, imageId: number) => void;
  onSetPrincipalImage: (variantId: string, imageId: number) => void;
  onReorderImages: (variantId: string, imageId: number, direction: 'up' | 'down') => void;
  setEditingVariant: React.Dispatch<React.SetStateAction<Variant | null>>;
  setNewProduct: React.Dispatch<React.SetStateAction<{ nombre: string; images: ProductImage[] }>>;
  onDeleteVariant?: (variantId: string) => void;
  rubro?: string;
}

// Temporary Image Manager component for handling images before uploading
export const TempImageManager = ({
  images,
  imageError,
  onChange,
  rubro
}: {
  images: ProductImage[],
  imageError: string | null,
  onChange: (images: ProductImage[]) => void,
  rubro?: string
}) => {
  // Determine which images and instructions to show based on rubro
  const isFashion = rubro === 'Moda e Indumentaria';
  const isHomeDecor = rubro === 'Hogar y Decoración';
  
  const getExampleImages = () => {
    if (isFashion) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto siendo utilizado'
      };
    } else if (isHomeDecor) {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    } else {
      return {
        img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png',
        img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png',
        alt1: 'Ejemplo de foto de producto',
        alt2: 'Ejemplo de foto de producto en contexto'
      };
    }
  };
  
  const exampleImages = getExampleImages();
  const [showLastImageWarning, setShowLastImageWarning] = useState(false);
  const [convertingHeic, setConvertingHeic] = useState(false);
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Validate file size (limit: 100 MB)
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      if (file.size > MAX_FILE_SIZE) {
        alert('El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.');
        e.target.value = '';
        return;
      }
      const newImageId = -1 * (images.length + 1); // Use negative IDs for temp images
      const isPrincipal = false; // Never set new images as principal by default
      
      try {
        // Check if the file is a HEIC image
        let processedFile = file;
        let processedUrl = '';
        
        if (isHeicFile(file)) {
          try {
            setConvertingHeic(true);
            const converted = await convertHeicToJpeg(file, 0.8);
            processedFile = converted.file;
            processedUrl = converted.url;
          } catch (err) {
            throw new Error('No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg');
          } finally {
            setConvertingHeic(false);
          }
        } else {
          processedUrl = URL.createObjectURL(file);
        }
        
        // Create new temp image entry
        const newImage: ProductImage = {
          id: newImageId,
          url: processedUrl,
          principal: isPrincipal,
          orden: images.length + 1,
          producto_variante_id: 0,
          tempFile: processedFile
        };
        
        onChange([...images, newImage]);
      } catch (error) {
        setConvertingHeic(false);
        console.error('Error processing image file:', error);
        alert(error instanceof Error ? error.message : 'Error al procesar la imagen. Por favor, intente con otra imagen.');
      }
      
      e.target.value = ''; // Reset input
    }
  };
  
  const handleDelete = (imageId: number) => {
    // Check if this is the last image
    if (images.length <= 1) {
      setShowLastImageWarning(true);
      return;
    }
    
    // Filter out the image to delete
    const updatedImages = images.filter(img => img.id !== imageId);
    
    // If we deleted the principal image and there are no principal images left, set the first one as principal
    if (updatedImages.length > 0 && !updatedImages.some(img => img.principal)) {
      updatedImages[0].principal = true;
    }
    
    onChange(updatedImages);
    
    // Revoke object URL to avoid memory leaks
    const deletedImg = images.find(img => img.id === imageId);
    if (deletedImg && deletedImg.id < 0) { // Only for temp images
      URL.revokeObjectURL(deletedImg.url);
    }
  };
  
  const handleSetPrincipal = (imageId: number) => {
    const updatedImages = images.map(img => ({
      ...img,
      principal: img.id === imageId
    }));
    
    onChange(updatedImages);
  };
  
  return (
    <div className="mt-4">
      <div className="mb-3">
        <input
          type="file"
          id="temp-image-upload"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
        {/* Guidance panel: correct vs incorrect examples */}
        {images.length === 0 && (
          <div className="mt-3 bg-surface-card border border-neutral-200 rounded-lg p-3 sm:p-4">
            <div className="flex flex-col gap-3">
              {/* Thumbnails row */}
              <div className="w-full flex items-center gap-3">
                <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                  <div className="w-full aspect-square rounded-lg border border-neutral-200 bg-surface-card overflow-hidden relative">
                    <img
                      src={exampleImages.img1}
                      alt={exampleImages.alt1}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                    <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col items-center gap-0">
                  <div className="w-full aspect-square rounded-lg border border-neutral-200 bg-surface-card overflow-hidden relative">
                    <img
                      src={exampleImages.img2}
                      alt={exampleImages.alt2}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                    <span aria-label="Correcta" className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </div>
              {/* Instructions (inline with thumbnails on desktop, below on mobile) */}
              <ul className="text-[length:var(--text-caption)] text-neutral-600 list-disc pl-4 space-y-1">
                <li>Subí una foto en donde se vea claramente el producto que querés vender.</li>
                {isFashion && <li>Fotos del producto siendo utilizado (sin mostrar el rostro) funcionan mejor.</li>}
              </ul>
            </div>
          </div>
        )}

        {/* Converting HEIC indicator */}
        {convertingHeic && (
          <div className="mt-3 flex items-center justify-center gap-3 p-4 border border-neutral-200 rounded-md bg-surface-card">
            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
            <p className="text-[length:var(--text-body)] text-neutral-600">Subiendo imagen...</p>
          </div>
        )}
        {/* Upload button below instructions; full width on mobile */}
        <div className="mt-3">
          <label
            htmlFor="temp-image-upload"
            className={`inline-flex w-full sm:w-auto justify-center items-center h-[var(--btn-h)] px-4 shadow-sm text-[length:var(--text-body)] font-medium rounded-md cursor-pointer transition-colors ${convertingHeic ? 'pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card' : images.length === 0 ? 'bg-brand-primary text-white hover:bg-brand-primary-hover' : 'border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100'}`}
          >
            <ImagePlus size={16} className="mr-2" />
            {convertingHeic ? 'Subiendo...' : images.length === 0 ? 'Subir imagen del producto' : 'Agregar otra imagen'}
          </label>
        </div>
      </div>
      
      {imageError && (
        <div className="mb-3 text-[length:var(--text-body)] text-error">{imageError}</div>
      )}
      
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {images.map((image) => (
            <div key={image.id} className="relative border border-neutral-200 rounded-lg overflow-hidden">
              <div className="relative">
                <img 
                  src={image.url} 
                  alt="Imagen de producto"
                  className="w-full h-40 object-cover"
                />
                {image.principal && (
                  <div className="absolute top-2 left-2 bg-brand-primary text-white text-[length:var(--text-caption)] px-2 py-0.5 rounded-md font-medium">
                    Principal
                  </div>
                )}
              </div>
              <div className="p-2 bg-surface-card flex justify-between">
                <div className="flex space-x-2">
                  {!image.principal && (
                    <button
                      type="button"
                      onClick={() => handleSetPrincipal(image.id)}
                      title="Establecer como principal"
                      className="text-neutral-400 hover:text-brand-primary transition-colors"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(image.id)}
                    title="Eliminar imagen"
                    className="text-neutral-400 hover:text-error transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Last Image Warning */}
      {showLastImageWarning && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-50">
          <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-md w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">No se puede eliminar</h3>
              <button 
                onClick={() => setShowLastImageWarning(false)} 
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <div className="w-10 h-10 rounded-full bg-warning-soft flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="w-5 h-5 text-warning" />
              </div>
              <p className="text-[length:var(--text-body)] text-neutral-600 text-center">El producto debe tener al menos una imagen. No se puede eliminar la última imagen.</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowLastImageWarning(false)}
                className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VariantForm: React.FC<VariantFormProps> = ({
  editingVariant,
  newProduct,
  loading,
  uploadingImages,
  imageError,
  onCancel,
  onSave,
  onImageUpload,
  onDeleteImage,
  onSetPrincipalImage,
  onReorderImages,
  setEditingVariant,
  setNewProduct,
  onDeleteVariant,
  rubro
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = useCallback(() => {
    setAnimated(false);
    setTimeout(() => {
      // If we're adding a new product (not editing), clear temporary state
      if (!editingVariant) {
        try {
          newProduct.images.forEach((img) => {
            if (img.id < 0 && img.url && img.url.startsWith('blob:')) {
              URL.revokeObjectURL(img.url);
            }
          });
        } catch (e) {
          // No-op
        }
        setNewProduct({ nombre: '', images: [] });
      }
      onCancel();
    }, 300);
  }, [editingVariant, newProduct, setNewProduct, onCancel]);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) handleClose();
    else setDragY(0);
  };

  // Determine if save is disabled (used for styling)
  const isSaveDisabled = editingVariant ? !editingVariant.nombre : !newProduct.nombre || loading;

  const handleSaveWithValidation = () => {
    const name = editingVariant ? editingVariant.nombre : newProduct.nombre;
    if (!name?.trim()) {
      setNameError(true);
      setShakeKey(k => k + 1);
      return;
    }
    onSave();
  };

  const title = editingVariant ? 'Editar producto' : 'Agregar producto';

  /* ── Shared form content ── */
  const formContent = (
    <div className="space-y-4">
      <div>
        <label className="block text-[length:var(--text-body)] font-medium text-neutral-700 mb-1">
          Nombre
        </label>
        <input
          type="text"
          value={editingVariant ? editingVariant.nombre : newProduct.nombre}
          onChange={(e) => {
            if (editingVariant) {
              setEditingVariant({ ...editingVariant, nombre: e.target.value });
            } else {
              setNewProduct({ ...newProduct, nombre: e.target.value });
            }
          }}
          key={shakeKey}
          className={`w-full h-[var(--btn-h)] px-4 border rounded-md text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors ${nameError ? 'border-error animate-shake' : 'border-neutral-200'}`}
          onFocus={() => nameError && setNameError(false)}
          placeholder="Nombre del producto"
        />
      </div>

      <div>
        <label className="block text-[length:var(--text-body)] font-medium text-neutral-700 mb-1">
          Imágenes
        </label>
        {editingVariant ? (
          <VariantImageManager
            variantId={editingVariant.id || ''}
            images={editingVariant.images || []}
            uploadingImages={uploadingImages}
            imageError={imageError}
            onUpload={onImageUpload}
            onDelete={onDeleteImage}
            onSetPrincipal={onSetPrincipalImage}
            onReorder={onReorderImages}
            showHeader={false}
          />
        ) : (
          <TempImageManager
            images={newProduct.images}
            imageError={imageError}
            onChange={(newImages) => setNewProduct({...newProduct, images: newImages})}
            rubro={rubro}
          />
        )}
      </div>
    </div>
  );

  /* ── Shared action buttons ── */
  const deleteButton = editingVariant && onDeleteVariant ? (
    <>
      {/* Mobile: icon-only */}
      <button
        onClick={() => setShowDeleteConfirm(true)}
        disabled={loading}
        className={`sm:hidden h-[var(--btn-h)] w-[var(--btn-h)] inline-flex items-center justify-center bg-error-soft hover:bg-error text-error hover:text-white border border-error/20 rounded-md shadow-sm transition-colors ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
        title="Eliminar producto"
      >
        <Trash className="w-4 h-4" />
      </button>
      {/* Desktop: full button */}
      <button
        onClick={() => setShowDeleteConfirm(true)}
        disabled={loading}
        className={`hidden sm:inline-flex h-[var(--btn-h)] px-4 items-center justify-center gap-2 text-[length:var(--text-body)] font-medium rounded-md shadow-sm transition-colors bg-error hover:bg-red-600 text-white ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
      >
        Eliminar producto
      </button>
    </>
  ) : <span />;


  /* ── Delete confirmation modal ── */
  const deleteConfirmModal = editingVariant && onDeleteVariant && showDeleteConfirm ? (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[70]">
      <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">Eliminar producto</h3>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mb-6">
          <div className="w-10 h-10 rounded-full bg-error-soft flex items-center justify-center mx-auto mb-3">
            <AlertCircle className="w-5 h-5 text-error" />
          </div>
          <p className="text-[length:var(--text-body)] text-neutral-600 text-center">¿Seguro que querés eliminar este producto? Esta acción no se puede deshacer.</p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => { onDeleteVariant(editingVariant.id); setShowDeleteConfirm(false); }}
            disabled={loading}
            className={`h-[var(--btn-h)] px-5 inline-flex items-center justify-center rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-error hover:bg-red-600 text-white ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 ${animated ? 'bg-black/40 opacity-100' : 'bg-black/40 opacity-0'}`}
        onClick={handleClose}
      />

      {/* Desktop: slide from right */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] z-[61] flex-col bg-surface-card rounded-[13px] shadow-lg overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-black/90">{title}</h3>
          <button onClick={handleClose} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-black/40 hover:bg-black/[0.1] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {formContent}
        </div>
        <div className="px-5 py-3 flex items-center justify-between shrink-0">
          {deleteButton}
          <div className="flex gap-3">
            <button type="button" onClick={handleClose} className="text-[15px] text-[#3C3C43]/60 hover:text-black/80 px-3 py-2 transition-colors">Cancelar</button>
            <button type="button" onClick={handleSaveWithValidation} disabled={loading} className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${isSaveDisabled ? 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed' : 'text-white bg-brand-primary hover:bg-brand-primary-hover'}`}>
              {loading ? 'Guardando...' : (editingVariant ? 'Listo' : 'Guardar')}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: bottom sheet */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[61] bg-[#F2F2F7] rounded-t-[13px] shadow-lg flex flex-col"
        style={{
          height: 'calc(100dvh - 1rem)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          transform: dragY > 0
            ? `translateY(${dragY}px)`
            : animated ? 'translateY(0)' : 'translateY(100%)',
          transition: dragY === 0 ? 'transform 0.3s ease-out' : 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div
          className="py-2 cursor-grab active:cursor-grabbing shrink-0"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button type="button" onClick={handleClose} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
          <h3 className="text-[15px] font-semibold text-black/90">{title}</h3>
          <button type="button" onClick={handleSaveWithValidation} disabled={loading} className={`text-[15px] font-semibold w-20 text-right active:opacity-50 transition-opacity ${isSaveDisabled ? 'text-black/20 cursor-not-allowed' : 'text-[#1644EB]'}`}>
            {loading ? 'Guardando' : (editingVariant ? 'Listo' : 'Guardar')}
          </button>
        </div>
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {formContent}
        </div>
        {/* Footer - only shown when delete is available */}
        {editingVariant && onDeleteVariant && (
          <div className="px-4 py-3 flex items-center justify-center shrink-0">
            {deleteButton}
          </div>
        )}
      </div>

      {deleteConfirmModal}
    </>,
    document.body
  );
};

export default VariantForm;
